import { BaseApiService } from './BaseApiService';
import { ApiResponse } from './ApiResponse';
export interface ApplicationMessageDTO {
  messageId: number;
  applicationId: string;
  senderUserId: number;
  senderName: string;
  senderRole: 'STUDENT' | 'ORGANIZATION' | 'MENTOR';
  messageContent: string;
  messageType: 'TEXT' | 'SYSTEM';
  parentMessageId?: number;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SendApplicationMessageRequest {
  applicationId: string;
  messageContent: string;
  messageType?: 'TEXT' | 'SYSTEM';
  parentMessageId?: number;
}

class ApplicationMessageService extends BaseApiService {
  
  /**
   * Send a message for an application
   */
  static async sendMessage(request: SendApplicationMessageRequest): Promise<ApiResponse<ApplicationMessageDTO>> {
    try {
      const response = await this.postAuthenticated(
        '/application-messages/send',
        request,
        'Send application message'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to send message');
    }
  }

  /**
   * Get all messages for an application
   */
  static async getMessagesByApplicationId(applicationId: string): Promise<ApiResponse<ApplicationMessageDTO[]>> {
    try {
      const response = await this.getAuthenticated(
        `/application-messages/application/${applicationId}`,
        'Get application messages'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get messages');
    }
  }

  /**
   * Get messages with threading for an application
   */
  static async getMessagesWithThreading(applicationId: string): Promise<ApiResponse<ApplicationMessageDTO[]>> {
    try {
      const response = await this.getAuthenticated(
        `/application-messages/application/${applicationId}/threaded`,
        'Get threaded application messages'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get threaded messages');
    }
  }

  /**
   * Mark messages as read for an application
   */
  static async markMessagesAsRead(applicationId: string, userRole: 'STUDENT' | 'ORGANIZATION'): Promise<ApiResponse<string>> {
    try {
      const response = await this.putAuthenticated(
        `/application-messages/application/${applicationId}/mark-read?userRole=${userRole}`,
        {},
        'Mark messages as read'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to mark messages as read');
    }
  }

  /**
   * Get unread message count for an application
   */
  static async getUnreadMessageCount(applicationId: string, userRole: 'STUDENT' | 'ORGANIZATION'): Promise<ApiResponse<number>> {
    try {
      const response = await this.getAuthenticated(
        `/application-messages/application/${applicationId}/unread-count?userRole=${userRole}`,
        'Get unread message count'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get unread message count');
    }
  }

  /**
   * Get unread messages for an application
   */
  static async getUnreadMessages(applicationId: string, userRole: 'STUDENT' | 'ORGANIZATION'): Promise<ApiResponse<ApplicationMessageDTO[]>> {
    try {
      const response = await this.getAuthenticated(
        `/application-messages/application/${applicationId}/unread?userRole=${userRole}`,
        'Get unread messages'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get unread messages');
    }
  }
}

export default ApplicationMessageService;
